import React, { useEffect, useState } from 'react';
import { KatAlert } from '@amzn/katal-react';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { setShowMarketplaceBanner } from 'src/redux/slices/appNotifications';
import { useRouter } from 'next/router';
import DOMPurify from 'isomorphic-dompurify';
import { customFetch, getMpRegionFromId, useTranslation } from '../utils';
import { GLOBAL_BANNER_API } from '../constants/apiEndpoints';

interface GlobalBannersState {
  header: string;
  description: string;
}
interface GlobalBannerAPIResponse {
  region: Record<string, GlobalBannersState>
  marketplace: Record<string, GlobalBannersState>
}

const GlobalBanners = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { replace, pathname, query } = useRouter();
  const [banner, setBanner] = useState<GlobalBannersState>();
  const showMarketplaceBanner = useAppSelector(
    ({ appNotifications }: AppState) => appNotifications.showMarketplaceBanner,
  );
  const {
    selectedMarketplaceId,
    previousSelectedMarketplaceId,
  } = useAppSelector(({ userInfo }: AppState) => userInfo);

  const getBannerData = (bannerResponse: GlobalBannerAPIResponse): GlobalBannersState => {
    if (bannerResponse.marketplace && bannerResponse.marketplace[selectedMarketplaceId]) {
      return bannerResponse.marketplace[selectedMarketplaceId];
    }
    const regionName = getMpRegionFromId(selectedMarketplaceId);
    if (bannerResponse.region && regionName && bannerResponse.region[regionName]) {
      return bannerResponse.region[regionName];
    }
    return { header: '', description: '' };
  };

  useEffect(() => {
    const updateBannerData = async () => {
      const bannerResponse = await customFetch(
        GLOBAL_BANNER_API,
        undefined,
      ) as GlobalBannerAPIResponse;
      const bannerData = getBannerData(bannerResponse);
      if (bannerData
        && typeof bannerData.header === 'string'
        && typeof bannerData.description === 'string') {
        setBanner(bannerData);
      }
    };

    updateBannerData();
  }, [selectedMarketplaceId]);

  return (
    <div className="p-0 text-sm">
      {banner && banner.header
        && (
          <div className="mb-1 bg-kat-lake">
            <KatAlert
              data-testid="global-banner"
              variant="info"
              header={banner.header}
              className="container"
              persistent
            >
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.description) }} />
            </KatAlert>
          </div>
        )}
      {showMarketplaceBanner && i18n.exists(`marketplace_id_${previousSelectedMarketplaceId}`)
        && (
          <div className="bg-kat-lake">
            <KatAlert
              variant="info"
              data-testid="global-marketplace-banner"
              header={t('marketplace_global_banner_title')}
              description={t('marketplace_global_banner_content', {
                currentMarketplace: t(`marketplace_id_${selectedMarketplaceId}`),
                previousMarketplace: t(`marketplace_id_${previousSelectedMarketplaceId}`),
              })}
              className="container"
              onDismiss={() => {
                dispatch(setShowMarketplaceBanner(false));
                if (query.prevMarketplace) {
                  delete query.prevMarketplace;
                  replace({ pathname, query }, undefined, { shallow: true });
                }
              }}
            />
          </div>

        )}
    </div>
  );
};

export default GlobalBanners;
